import { gql, NetworkStatus, useQuery } from '@apollo/client'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  FetchNewNotificationsQuery,
  FetchNewNotificationsQueryVariables,
  NotificationBoxFragmentDoc,
  OfficialInformationQuery,
  OfficialInformationQueryVariables,
  OrganizationNotificationConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { isFirstLoadRef } = useGetNewestData<
    FetchNewNotificationsQuery,
    FetchNewNotificationsQueryVariables
  >({
    query: QUERY,
    variables: { pageInput: { limit: 20 } },
    fields: [
      {
        fieldName: 'notifications',
        fragment: NotificationBoxFragmentDoc,
      },
    ],
  })

  const { data, loading, networkStatus, fetchMore } = useQuery<
    OfficialInformationQuery,
    OfficialInformationQueryVariables
  >(QUERY, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: { pageInput: { limit: 5 } },
    onCompleted() {
      isFirstLoadRef.current = false
    },
  })

  const isNextFetch = !!data

  return {
    state: {
      loading: loading && !isNextFetch,
      refetching: networkStatus === NetworkStatus.fetchMore,
      hasNextPage:
        !!data?.organizationOfficialNotifications?.pageInfo.hasNextPage,
      notifications: data?.organizationOfficialNotifications ?? null,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            pageInput: {
              limit: 5,
              cursor:
                data?.organizationOfficialNotifications?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query OfficialInformation($pageInput: PageInput) {
    organizationOfficialNotifications(pageInput: $pageInput) {
      ...OrganizationNotificationConnection
    }
  }
  ${OrganizationNotificationConnectionFragmentDoc}
`
